import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { authHeader } from '../../utils/authHeader';

const PAGE_SIZE = 25;

// Same accumulating "load more" pattern as useGACompaniesPage. There is no
// server-side search on /ga/support/messages (the inbox is meant to be
// scanned, not queried), so the search box filters only the loaded window.
const useGASupportMessagesPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle');

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchMessages = useCallback((reset = true) => {
    if (!config?.backendUrl) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    axios.get(`${config.backendUrl}/ga/support/messages`, {
      params: { page, pageSize: PAGE_SIZE },
      headers: authHeader(),
    })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching support messages', error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /ga/support/messages · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, t]);

  useEffect(() => { fetchMessages(true); }, [fetchMessages]);

  const loadMore = () => fetchMessages(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const openDetail = (row) => {
    setDetail(row);
    setDetailStatus('loading');
    axios.get(`${config.backendUrl}/ga/support/messages/${row._id}`, { headers: authHeader() })
      .then((res) => {
        setDetail(res.data);
        setDetailStatus('ready');
      })
      .catch(() => setDetailStatus('error'));
  };
  const closeDetail = () => {
    setDetail(null);
    setDetailStatus('idle');
  };

  const query = search.trim().toLowerCase();
  const filteredRows = query
    ? rows.filter((m) => [m.message, m.companyName, m.username, m.context?.route]
      .some((field) => (field || '').toLowerCase().includes(query)))
    : rows;

  const isNoMatch = status === 'ready' && Boolean(query) && filteredRows.length === 0;

  return {
    t, total, errorInfo, status, rows: filteredRows, isNoMatch, search,
    searchInput, setSearchInput,
    loadingMore, loadMore, remaining, nextBatchSize, fetchMessages,
    detail, detailStatus, openDetail, closeDetail,
  };
};

export default useGASupportMessagesPage;
